/**
 * useCompanyBranding — Haalt bedrijfsbranding op en past CSS-variabelen toe.
 */
import { useEffect, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;

export const DEFAULT_BRANDING = {
  primary_color: "#C9A84C",
  secondary_color: "#0f0f0f",
  accent_color: "#ffffff",
  text_color: "#ffffff",
  bg_color: "#080808",
  nav_color: "#0a0a0a",
  card_color: "#0f0f0f",
  card2_color: "#1a1a1a",
  border_color: "#1f1f1f",
  logo_url: "",
};

export function useCompanyBranding(user) {
  const [branding, setBranding] = useState(null);
  const [companyNaam, setCompanyNaam] = useState("");

  useEffect(() => {
    if (!user?.token) return;
    if (!["sporter", "company_admin"].includes(user.role)) return;

    const fetchBranding = async () => {
      try {
        const r = await fetch(`${API}/api/sporter/company-info`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!r.ok) return;
        const data = await r.json();
        if (data.branding) {
          setBranding(data.branding);
          setCompanyNaam(data.naam || "");
          applyBrandingCSS(data.branding);
        }
      } catch {}
    };

    fetchBranding();

    return () => { resetBrandingCSS(); };
  }, [user?.token, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  return { branding: branding || DEFAULT_BRANDING, companyNaam };
}

/**
 * Berekent de relatieve luminantie van een hex-kleur (0 = zwart, 1 = wit).
 * Wordt gebruikt om te bepalen of een achtergrond licht of donker is.
 */
function _luminance(hex) {
  try {
    const h = hex.replace("#", "");
    const r = parseInt(h.substr(0, 2), 16) / 255;
    const g = parseInt(h.substr(2, 2), 16) / 255;
    const b = parseInt(h.substr(4, 2), 16) / 255;
    return 0.299 * r + 0.587 * g + 0.114 * b;
  } catch { return 0; }
}

/**
 * Past CSS-variabelen toe voor volledige whitelabel theming van het company dashboard.
 *
 * Strategie:
 *  1. Zet --bc-* CSS custom properties op :root (voor gebruik in App.css regels)
 *  2. Injecteer een <style> tag met klasse-overrides voor alle achtergronden/borders
 *     Tailwind escapet '#' als '\#' in class selectors, dus we gebruiken \\# in de template literal
 */
export function applyBrandingCSS(branding) {
  if (!branding) return;
  const root = document.documentElement;

  const primary  = branding.primary_color  || "#C9A84C";
  const bgColor  = branding.bg_color       || branding.secondary_color || "#080808";
  const navColor = branding.nav_color      || "#0a0a0a";
  const card     = branding.card_color     || "#0f0f0f";
  const card2    = branding.card2_color    || "#1a1a1a";
  const border   = branding.border_color   || "#1f1f1f";
  const text     = branding.text_color     || "#ffffff";

  // Legacy CSS variabelen
  root.style.setProperty("--brand-primary", primary);
  root.style.setProperty("--brand-bg",      bgColor);
  root.style.setProperty("--brand-accent",  branding.accent_color || "#ffffff");

  // Uitgebreide CSS custom properties — gebruikt in App.css [data-company-branding] regels
  root.style.setProperty("--bc-bg",      bgColor);
  root.style.setProperty("--bc-nav",     navColor);
  root.style.setProperty("--bc-card",    card);
  root.style.setProperty("--bc-card2",   card2);
  root.style.setProperty("--bc-border",  border);
  root.style.setProperty("--bc-text",    text);
  root.style.setProperty("--bc-primary", primary);

  // Bereken contrasttekstkleur voor kaartachtergrond (donker vs. licht)
  const cardIsLight = _luminance(card) > 0.45;
  const bgIsLight   = _luminance(bgColor) > 0.45;
  root.style.setProperty("--bc-card-text", cardIsLight ? "#111111" : "#ffffff");
  root.style.setProperty("--bc-bg-text",   bgIsLight   ? "#111111" : "#ffffff");

  // Kritieke fix: [data-theme="dark"] op #dashboard-root definieert --bc-bg: #080808 via CSS
  // en blokkeert daarmee de cascade van <html>. We overschrijven het via een inline style op
  // #dashboard-root zelf zodat var(--bc-bg) het juiste bedrijfskleuren oplost.
  const dashRoot = document.getElementById("dashboard-root");
  if (dashRoot) {
    dashRoot.style.setProperty("--bc-bg",        bgColor);
    dashRoot.style.setProperty("--bc-nav",        navColor);
    dashRoot.style.setProperty("--bc-card",       card);
    dashRoot.style.setProperty("--bc-card2",      card2);
    dashRoot.style.setProperty("--bc-border",     border);
    dashRoot.style.setProperty("--bc-text",       text);
    dashRoot.style.setProperty("--bc-primary",    primary);
    const cardIsLight2 = _luminance(card) > 0.45;
    const bgIsLight2   = _luminance(bgColor) > 0.45;
    dashRoot.style.setProperty("--bc-card-text",  cardIsLight2 ? "#111111" : "#ffffff");
    dashRoot.style.setProperty("--bc-bg-text",    bgIsLight2   ? "#111111" : "#ffffff");
  }

  // Verwijder eerdere injectie
  const old = document.getElementById("__company-branding-style__");
  if (old) old.remove();

  // Kleine aanvullende injectie voor hoge-specificiteit overrides
  // (App.css bevat het merendeel via [data-company-branding] attribuut selectors)
  const style = document.createElement("style");
  style.id = "__company-branding-style__";
  style.textContent = `
    /* Hoge specificiteit voor [data-theme="light"] overrides */
    #dashboard-root [data-company-branding] {
      background: var(--bc-bg) !important;
    }
    #dashboard-root [data-company-branding] .company-nav {
      background: var(--bc-nav) !important;
      border-bottom-color: var(--bc-border) !important;
    }
    #dashboard-root [data-company-branding] .text-white {
      color: var(--bc-text) !important;
    }
    #dashboard-root [data-company-branding] *[class*="border-white/"] {
      border-color: var(--bc-border) !important;
    }
  `;
  document.head.appendChild(style);
}

export function resetBrandingCSS() {
  const vars = ["--brand-primary","--brand-bg","--brand-accent",
    "--bc-bg","--bc-nav","--bc-card","--bc-card2","--bc-border","--bc-text","--bc-primary"];
  const root = document.documentElement;
  vars.forEach(v => root.style.removeProperty(v));
  // Ook van #dashboard-root verwijderen (zie applyBrandingCSS)
  const dashRoot = document.getElementById("dashboard-root");
  if (dashRoot) vars.forEach(v => dashRoot.style.removeProperty(v));
  const style = document.getElementById("__company-branding-style__");
  if (style) style.remove();
}
